import { defineStore } from "pinia"
import { colorMapper } from "~/constants/common"
import { USER_POSITION } from "/constants/common.js"

export const useStatisticStore = defineStore("statistic", () => {
  const { locale } = useI18n()
  const waitingFromApex = ref(null)
  const viewedRankTable = ref(null)
  const favoriteRankTable = ref(null)
  const downloadRankTable = ref(null)
  const viewList = ref(null)
  const viewDomainList = ref(null)
  const viewListCount = ref(0)
  const downloadList = ref(null)
  const downloadDomainList = ref(null)
  const downloadListCount = ref(0)
  const favoriteList = ref(null)
  const favoriteDomainList = ref(null)
  const favoriteListCount = ref(0)
  const apexCompanyList = ref(null)
  const apexCompanyDomainList = ref(null)
  const apexCompanyCount = ref(0)
  const apexColorList = ref(null)
  const apexColorCount = ref(0)
  const apexCompanyTime = ref(0)
  const viewedTime = ref(0)
  const downloadedTime = ref(0)
  const favoriteTime = ref(0)
  const drawChart = ref(false)
  const colorRank = ref(null)
  const materialRank = ref(null)
  const colorChart = ref(null)
  const materialChart = ref(null)
  const countRank = ref(null)
  const gaugeRank = ref(null)
  const priceRank = ref(null)
  const countChart = ref(null)
  const gaugeChart = ref(null)
  const priceChart = ref(null)
  const apexUsedRank = ref(null)
  const userRole = ref(null)
  const bookSearchCondition = ref({})
  const bookVersions = ref(null)
  const bookByCompany = ref(null)
  const viewChart = ref(null)
  const favoriteChart = ref(null)
  const downloadChart = ref(null)
  const apexUserChart = ref(null)
  const apexLogList = ref(null)
  const apexLogListCount = ref(0)

  const getApexLogList = async (params) => {
    const { data } = await usePost('/used/q/a', { locale: locale.value, ...params })
    apexLogList.value = data.data.data
    apexLogListCount.value = data.data.totalCount

    return {
      apexLogList: apexLogList.value,
      apexLogListCount: apexLogListCount.value
    }
  }

  const setStatisticTimes = ({ viewed, downloaded, favorite, apexCompany }) => {
    viewedTime.value = viewed
    downloadedTime.value = downloaded
    favoriteTime.value = favorite
    apexCompanyTime.value = apexCompany
  }

  const viewedChart = (val) => {
    return viewedRankTable.value ? viewedRankTable.value.map(({ bookName, ratio }) => {
      return {
        category: bookName,
        value: ratio ? ratio.split('%')[0] : 0
      }
    }) : []
  }

  const favoritedChart = (val) => {
    return favoriteRank.value ? favoriteRank.value.map(({ bookName, ratio }) => {
      return {
        category: bookName,
        value: ratio ? ratio.split('%')[0] : 0
      }
    }) : []
  }

  const downloadedChart = (val) => {
    return downloadRank.value ? downloadRank.value.map(({ bookName, ratio }) => {
      return {
        category: bookName,
        value: ratio ? ratio.split('%')[0] : 0
      }
    }) : []
  }

  const setApexColorList = ({ list, listCount }) => {
    apexColorList.value = list
    apexColorCount.value = listCount
  }


  const setMarketTrendValues = ({
    colorRankData,
    materialRankData,
    colorChartData,
    materialChartData,
    countRankData,
    gaugeRankData,
    priceRankData,
    countChartData,
    gaugeChartData,
    priceChartData
  }) => {
    colorRank.value = colorRankData
    materialRank.value = materialRankData
    colorChart.value = colorChartData
    materialChart.value = materialChartData
    countRank.value = countRankData
    gaugeRank.value = gaugeRankData
    priceRank.value = priceRankData
    countChart.value = countChartData
    gaugeChart.value = gaugeChartData
    priceChart.value = priceChartData
  }

  const setBookSearchCondition = (target) => {
    bookSearchCondition.value = target
  }
    
  const setRankList = ({ viewedRankData, favoriteRankData, downloadRankData, apexUseRankData, userRoleData }) => {
    viewedRankTable.value = viewedRankData
    favoriteRankTable.value = favoriteRankData
    downloadRankTable.value = downloadRankData
    apexUsedRank.value = apexUseRankData
    userRole.value = userRoleData
  }

  const setBookByCompany = (target) => {
    bookByCompany.value = target 
  }

  const setBookVersions = (target) => {
    bookVersions.value = target
  }

  const setRankChartList = ({ viewed, favorite, download, apexUser }) => {
    viewChart.value = viewed
    favoriteChart.value = favorite
    downloadChart.value = download
    apexUserChart.value = apexUser
  }

  const getViewList = async (params) => {
    const { data } = await usePost('/viewed/q/c', { locale: locale.value, ...params })
    let domainList = []
    if (params.grouping === 'Y') {
      domainList = groupDomain(data.data.data, 'viewedTime')
    }
    viewList.value = data.data.data
    viewListCount.value = data.data.totalCount
    viewDomainList.value = domainList
    return {
      viewList: viewList.value,
      viewListCount: viewListCount.value,
      viewDomainList: viewDomainList.value
    }
  }

  const getFavoriteList = async (params) => {
    const { data } = await usePost('/favorite/q/c', { locale: locale.value, ...params })
    let viewDomainList = []
    if (params.grouping === 'Y') {
      viewDomainList = groupDomain(data.data.data, 'favoriteTime')
    }
    favoriteList.value = data.data.data
    favoriteListCount.value = data.data.totalCount
    favoriteDomainList.value = viewDomainList
    return {
      favoriteList: favoriteList.value,
      favoriteListCount: favoriteListCount.value,
      favoriteDomainList: favoriteDomainList.value
    }
  }

  const getDownloadList = async (params) => {
    const { data } = await usePost('/download/q/c', { locale: locale.value, ...params })
    let viewDomainList = []
    if (params.grouping === 'Y') {
      viewDomainList = groupDomain(data.data.data, 'viewedTime')
    }
    downloadList.value = data.data.data
    downloadListCount.value = data.data.totalCount
    downloadDomainList.value = viewDomainList
    return {
      downloadList: downloadList.value,
      downloadListCount: downloadListCount.value,
      downloadDomainList: downloadDomainList.value
    }
  }

  const getApexCompanyList = async (params) => {
    const { data } = await usePost('/used/q/b/company', { locale: locale.value, ...params })
    let domainList = []
    if (params.grouping === 'Y') {
      domainList = groupDomain(data.data.data, 'usedTime')
    }
    apexCompanyList.value = data.data.data
    apexCompanyCount.value = data.data.totalCount
    apexCompanyDomainList.value = domainList
    return {
      apexCompanyList: apexCompanyList.value,
      apexCompanyCount: apexCompanyCount.value,
      apexCompanyDomainList: apexCompanyDomainList.value
    }
  }

  const getApexColorList = async (params) => {
    const {
      data: {
        data: { data: apexColorList, totalCount: apexColorCount }
      }
    } = await usePost('/used/q/b/color', { locale: locale.value, ...params })
    setApexColorList({ list: apexColorList, listCount: apexColorCount })
    return { apexColorList, apexColorCount }
  }

  const getYarnStatisticTimes = async ({ yarnId, fromDate = null, toDate = null, ...rest }) => {
    const baseStore = useBaseStore()
    return apiWrapper(async () => {
      baseStore.getUserMe()
      const [
        {
          data: {
            data: { totalCount: downloaded }
          }
        },
        {
          data: {
            data: { totalCount: viewed }
          }
        },
        {
          data: {
            data: { totalCount: favorite }
          }
        },
        {
          data: {
            data: { totalCount: apexCompany }
          }
        }
      ] = await Promise.all([
        usePost('/download/q/t', {
          ...rest,
          yarnId,
          fromDate,
          toDate,
          locale: locale.value,
        }),
        usePost('/viewed/q/t', {
          ...rest,
          yarnId,
          fromDate,
          toDate,
          locale: locale.value,
        }),
        usePost('/favorite/q/t', {
          ...rest,
          yarnId,
          fromDate,
          toDate,
          locale: locale.value,
        }),
        usePost('/used/q/b/company/t/a', {
          ...rest,
          yarnId,
          fromDate,
          toDate,
          locale: locale.value,
        }),
      ])
      setStatisticTimes({
        viewed,
        downloaded,
        favorite,
        apexCompany
      })
      return true
    })
  }

  const getRankList = async (params) => {
    const baseStore = useBaseStore()
    const authStore = useAuthStore()
    return apiWrapper(async () => {
      baseStore.getUserMe()
      const [
        {
          data: { data: viewedRank }
        },
        {
          data: { data: favoriteRank }
        },
        {
          data: { data: downloadRank }
        },
        {
           data: { data: apexUseRank }
        }
      ] = await Promise.all([
        usePost('/viewed/q/r', {
          ...params,
          locale: locale.value,
        }),
        usePost('/favorite/q/r', {
          ...params,
          locale: locale.value,
        }),
        usePost('/download/q/r', {
          ...params,
          locale: locale.value,
        }),
        usePost('/used/q/r', {
          ...params,
          locale: locale.value,
        })
      ])

      setRankList({
        viewedRankData: viewedRank,
        favoriteRankData: favoriteRank,
        downloadRankData: downloadRank,
        apexUseRankData: apexUseRank,
        userRole: authStore.user.userRole === USER_POSITION.DEMO
      })
      return true
    })
  }

  const getBookVersions = (defaultText) => {
    return bookVersions.value
    ? [
        defaultText,
        ...bookVersions.value.map(({ yarnNo }) => {
          return yarnNo
        })
      ]
    : []
  }

  const getAllBookVersion = async ({ ...params }) => {
    const { data : { data: bookVersions } } = await usePost('/yarn/report/q/v', { locale: locale.value, ...params })
    setBookVersions(bookVersions)
  }

  const getBookByCompany = async ({...params}) => {
    const { data: { data } } = await usePost('/yarn/report/q/c', { ...params, locale: locale.value })
    setBookByCompany(data.map(({ bookName, yarnlogId }) => {
      return {
        text: bookName,
        value: yarnlogId
      }
    }))
  }

  const getStatistic = (params) => {
    return apiWrapper(async () => {
      const { data, headers } = await usePost('/export/down/supplier',
        { ...params, locale: locale.value },
        { responseType: 'blob' })
      return { data, headers }
    })
  }

  const getRankChartList = (params) => {
    const baseStore = useBaseStore()
    return apiWrapper(async () => {
      baseStore.getUserMe()
      const {
        data: { data: viewedRankChart }
      } = await usePost('/viewed/q/r/chart', { locale: locale.value, ...params })

      const {
        data: { data: favoriteRankChart }
      } = await usePost('/favorite/q/r/chart', { locale: locale.value, ...params })

      const {
        data: { data: downloadRankChart }
      } = await usePost('/download/q/r/chart', { locale: locale.value, ...params })

      const {
        data: { data: apexUseRankChart }
      } = await usePost('/used/q/r/chart', { locale: locale.value, ...params })

      setRankChartList({
        viewed: viewedRankChart,
        favorite: favoriteRankChart,
        download: downloadRankChart,
        apexUser: Array.isArray(apexUseRankChart)
          ? apexUseRankChart.map(({ usedCount, ...rest }) => ({
              viewCount: usedCount,
              ...rest
            }))
          : [],
      })
    })
  }

  const getMarketRankList = async (filters) => {
    const baseStore = useBaseStore()
    const authStore = useAuthStore()
    const params = {
      limit: 7,
      demoFlag: authStore.user.userRole === USER_POSITION.DEMO ? 'Y' : 'N',
      ...filters
    }

    return apiWrapper(async () => {
      baseStore.getUserMe()
      const dataFromServer = await Promise.all([
        usePost('/trend/q/color', { locale: locale.value, ...params }),
        usePost('/trend/q/color/chart', { locale: locale.value, ...params }),
        usePost('/trend/q/material', { locale: locale.value, ...params }),
        usePost('/trend/q/material/chart', { locale: locale.value, ...params }),
        usePost('/trend/q/yarncount', { locale: locale.value, ...params }),
        usePost('/trend/q/yarncount/chart', { locale: locale.value, ...params }),
        usePost('/trend/q/gauge', { locale: locale.value, ...params }),
        usePost('/trend/q/gauge/chart', { locale: locale.value, ...params }),
        usePost('/trend/q/price', { locale: locale.value, ...params }),
        usePost('/trend/q/price/chart', { locale: locale.value, ...params }),
      ])

      const colorMap = colorMapper.reduce((previous, { codeId, color }) => {
        previous[codeId] = color
        return previous
      }, {})
      setMarketTrendValues({
        colorRankData: Array.isArray(dataFromServer[0].data.data)
          ? dataFromServer[0].data.data.map(
          ({ yarnColorId, ...rest }) => {
            return { ...rest, color: colorMap[yarnColorId] }
          })
          : [],
        colorChartData: Array.isArray(dataFromServer[1].data.data)
          ? dataFromServer[1].data.data.map(
          ({ yarnColorId, ...rest }) => {
            return { ...rest, color: colorMap[yarnColorId] }
          })
          : [],
        materialRankData: dataFromServer[2].data.data,
        materialChartData: dataFromServer[3].data.data,
        countRankData: dataFromServer[4].data.data,
        countChartData: dataFromServer[5].data.data,
        gaugeRankData: dataFromServer[6].data.data,
        gaugeChartData: dataFromServer[7].data.data,
        priceRankData: dataFromServer[8].data.data,
        priceChartData: dataFromServer[9].data.data
      })
      return true
    })
  }

  return {
    waitingFromApex,
    viewedRankTable,
    favoriteRankTable,
    downloadRankTable,
    viewList,
    viewDomainList,
    viewListCount,
    downloadList,
    downloadDomainList,
    downloadListCount,
    favoriteList,
    favoriteDomainList,
    favoriteListCount,
    apexCompanyList,
    apexCompanyDomainList,
    apexCompanyCount,
    apexColorList,
    apexColorCount,
    viewedTime,
    downloadedTime,
    favoriteTime,
    apexCompanyTime,
    drawChart,
    colorRank,
    materialRank,
    colorChart,
    materialChart,
    countRank,
    gaugeRank,
    priceRank,
    countChart,
    gaugeChart,
    priceChart,
    bookSearchCondition,
    apexUsedRank,
    userRole,
    bookVersions,
    bookByCompany,
    viewChart,
    favoriteChart,
    downloadChart,
    apexUserChart,
    apexLogList,
    apexLogListCount,
    viewedChart,
    favoritedChart,
    downloadedChart,
    setApexColorList,
    setStatisticTimes,
    setMarketTrendValues,
    setBookSearchCondition,
    setRankList,
    setBookByCompany,
    setBookVersions,
    setRankChartList,
    getViewList,
    getFavoriteList,
    getDownloadList,
    getApexCompanyList,
    getApexColorList,
    getYarnStatisticTimes,
    getRankList,
    getBookVersions,
    getBookByCompany,
    getStatistic,
    getAllBookVersion,
    getRankChartList,
    getMarketRankList,
    getApexLogList
  }
})